import { Page, Text, View } from '@react-pdf/renderer'
import { Button, Form, Input, message } from 'antd'
import { SectionHeader } from 'pages/reporting/reports/externalVisual/components/VisualInspectionSectionHeader'
import { BundleSubInspectionsData } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import GeckoLogo from 'pages/reporting/shared/pdfGeneration/components/shared/GeckoLogo'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useCallback, useMemo } from 'react'

const requiredBundleSubInspectionsFields = ['bundleSubInspection']

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useVisualReportOps()
  const data = useMemo(
    () => formData.bundleSubInspectionsData,
    [formData.bundleSubInspectionsData]
  )

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  return (
    <Page
      size='A4'
      style={PDFStyles.page}
      id='bundle-sub-inspections'
      wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'bundle-sub-inspections', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              Checklist Inspection Report
            </Text>
          </View>
        </View>

        {/* Section Header */}
        <SectionHeader title='Bundle Sub Inspections' />

        {/* Content */}
        <View style={{ marginTop: 10 }}>
          {data.bundleSubInspection ? (
            <Text style={{ fontSize: 10, lineHeight: 1.5 }}>
              {data.bundleSubInspection}
            </Text>
          ) : (
            <Text style={{ fontSize: 10 }}>No data to display.</Text>
          )}
        </View>
      </View>

      <PageFooter
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<BundleSubInspectionsData>()
  const { formData, handleBundleSubInspectionsFinish } = useVisualReportOps()

  const onFinish = useCallback(
    async (values: BundleSubInspectionsData) => {
      try {
        await handleBundleSubInspectionsFinish(values.bundleSubInspection || '')
        message.success('Bundle sub-inspections saved successfully!')
      } catch {
        message.error('Failed to save bundle sub-inspections')
      }
    },
    [handleBundleSubInspectionsFinish]
  )

  const initialValues = React.useMemo(
    () => formData.bundleSubInspectionsData,
    [formData.bundleSubInspectionsData]
  )

  return (
    <StyledForm<BundleSubInspectionsData>
      form={form}
      name='bundleSubInspectionsForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item
        name='bundleSubInspection'
        label='Bundle Sub-Inspection Details'
        rules={[
          {
            required: true,
            message: 'Please enter bundle sub-inspection details',
          },
        ]}>
        <Input.TextArea
          rows={6}
          placeholder='Enter bundle inspection details...'
        />
      </Form.Item>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Save Bundle Sub-Inspections
        </Button>
      </Form.Item>
    </StyledForm>
  )
}

export const BundleSubInspectionsSection: ReportSection = {
  RenderReact,
  required: false,
  RenderPDF,
  id: 'bundleSubInspections',
  description: 'Bundle inspection details',
  title: 'Bundle Sub-Inspections',
  requiredFields: requiredBundleSubInspectionsFields,
}

export const BundleSubInspectionsSectionPDF = RenderPDF
